/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */
import pipe from 'lodash/fp/pipe.js';
import get from 'lodash/fp/get.js';
import { connect } from 'react-redux';
import { SubmissionError } from 'redux-form';
import { modelTypesById } from '#src/js/constants/models/modelTypes/index.js';
import calculateModelPutPayload from '#support/models/calculateModelPutPayload.js';
import { fetchModel, postRun, putModel } from '#actions/models/model.js';
import withLocation from '#components/support/routing/withLocation/index.jsx';
import withNavigate from '#components/support/routing/withNavigate/index.jsx';
import { PROBLEM_WITH_REQUEST } from '#constants/errorMessages.js';
import { MODELS } from '#constants/pathStrings.js';

const withOnSubmit = C => {
  const mapStateToProps = (state, ownProps) => {
    const { modelType } = ownProps;
    const { selectAutofilledFormFields, selectFieldsVisibility, modelFormName } =
      modelTypesById[modelType];
    return {
      autofilled: selectAutofilledFormFields(state),
      fieldsVisibility: selectFieldsVisibility(state),
      // Marking is form state (UI design §3.4). The ordinary save/run path must carry it forward the
      // same way the debounced autosave does (makeFormMiddlware `save`) -- reading aiStatus/provenance
      // from the form slice -- or a routine Update-and-Run would strip every field's marking (the
      // marking-PUT regression). calculateModelPutPayload defaults any unmarked field to none/null.
      aiStatus: get(`form[${modelFormName}].aiStatus`, state) || {},
      provenance: get(`form[${modelFormName}].provenance`, state) || {},
    };
  };

  const mapDispatchToProps = (dispatch, ownProps) => {
    const { location, modelId, modelType, navigate } = ownProps;
    const { submitRedirectUrl, fields } = modelTypesById[modelType];
    return {
      makeOnSubmit: (autofilled, visibleFields, aiStatus, provenance) => values => {
        const payload = calculateModelPutPayload({
          autofilled,
          visibleFields,
          values,
          fields,
          aiStatus,
          provenance,
        });
        return dispatch(putModel({ uuid: modelId, payload }))
          .then(() => dispatch(postRun(modelId)))
          .then(() => dispatch(fetchModel(modelId)))
          .then(() => {
            if (submitRedirectUrl) {
              const [redirectGroupSegment] = submitRedirectUrl.split('/');
              const inRedirectGroup = location.pathname.split('/')[3] === redirectGroupSegment;
              if (!inRedirectGroup) {
                navigate(`/${MODELS}/${modelId}/${submitRedirectUrl}`);
              }
            }
          })
          .catch(() =>
            Promise.reject(
              new SubmissionError({
                _error: PROBLEM_WITH_REQUEST,
              })
            )
          );
      },
    };
  };

  const mergeProps = (stateProps, dispatchProps, ownProps) => {
    const { autofilled, fieldsVisibility, aiStatus, provenance } = stateProps;
    const { makeOnSubmit } = dispatchProps;
    return {
      ...ownProps,
      onSubmit: makeOnSubmit(autofilled, fieldsVisibility, aiStatus, provenance),
    };
  };

  return pipe(
    connect(mapStateToProps, mapDispatchToProps, mergeProps),
    withNavigate,
    withLocation
  )(C);
};

export default withOnSubmit;
